import { useState, useEffect, useRef } from 'react';
import { BaseDialog } from '@app/components';
import { Tooltip } from '@app/components/Tooltip/Tooltip';
import clsx from 'clsx';
import { useAppDispatch } from '@app/redux/hooks';
import { updateFormData } from '@app/redux/features/campaignForm';
import { getBusinessInfo } from '@app/services/business';
import { AIDropdownMinimal } from '@app/components/AIDropdown/AIDropdownMinimal';
import { VariableDropdown } from '@app/components/VariableDropdown/VariableDropdown';
import { 
  improveText, 
  fixGrammarAndSpelling, 
  makeTextConcise, 
  regenerateText,
  changeTone
} from '@app/services/service';
import ChangeToneIcon from '@app/assets/icons/ai/change-tone.svg?react';
import ConciseIcon from '@app/assets/icons/ai/concise.svg?react';
import FormatIcon from '@app/assets/icons/ai/format.svg?react';
import GrammarIcon from '@app/assets/icons/ai/grammar.svg?react';
import RegenerateIcon from '@app/assets/icons/ai/regenerate.svg?react';
import Edit03Icon from '@app/assets/icons/edit-03.svg?react';
import FilePhotoIcon from '@app/assets/illustrations/file-photo.svg?react';

// Helper function to insert variable at cursor position
const insertVariableAtCursor = (
  currentValue: string,
  variable: string,
  inputRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement>
): { newValue: string; cursorPosition: number } => {
  if (inputRef.current) {
    const input = inputRef.current;
    const start = input.selectionStart || 0;
    const end = input.selectionEnd || 0;
    const newValue = currentValue.slice(0, start) + variable + currentValue.slice(end);
    const cursorPosition = start + variable.length;
    return { newValue, cursorPosition };
  }
  const newValue = currentValue + variable;
  return { newValue, cursorPosition: newValue.length };
};

// Helper function to create tone options for Change tone dropdown
const createToneOptions = (
  value: string,
  handleAIAction: (action: () => Promise<any>) => Promise<void>
) => {
  const tones = [
    { label: 'Write appreciative', tone: 'appreciative' },
    { label: 'Write apologetic', tone: 'apologetic' },
    { label: 'Write informative', tone: 'informative' },
    { label: 'Write interactive', tone: 'interactive' },
    { label: 'Write humorous', tone: 'humorous' },
    { label: 'Write formal', tone: 'formal' },
    { label: 'Write casual', tone: 'casual' },
    { label: 'Write personalized', tone: 'personalized' }
  ];

  return tones.map(({ label, tone }) => ({
    label,
    Icon: <Edit03Icon className="w-4 h-4" />,
    onClick: () => handleAIAction(() => changeTone(value, tone))
  }));
};

// Individual form field components
const HeadingField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(value, variable, inputRef);
    onChange(newValue);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleAIAction = async (action: () => Promise<any>) => {
    if (!value.trim()) return;
    
    try {
      const response = await action();
      if (response?.data?.text) {
        onChange(response.data.text);
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  const aiActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleAIAction(() => regenerateText(value))
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(value, handleAIAction)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleAIAction(() => fixGrammarAndSpelling(value))
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleAIAction(() => makeTextConcise(value))
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleAIAction(() => improveText(value))
    }
  ];

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Heading</label>
      <div className="flex items-start gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-[30rem] p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          placeholder="Leave a review"
        />
        <div className="flex gap-1 items-center">
          <VariableDropdown onSelect={handleVariableSelect} />
          <div className="relative">
            <AIDropdownMinimal     
              actions={aiActions}
              onlyIcon={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const DescriptionField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleVariableSelect = (variable: string) => {
    const { newValue, cursorPosition } = insertVariableAtCursor(value, variable, inputRef);
    onChange(newValue);
    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        inputRef.current.focus();
      }
    }, 0);
  };

  const handleAIAction = async (action: () => Promise<any>) => {
    if (!value.trim()) return;
    
    try {
      const response = await action();
      if (response?.data?.text) {
        onChange(response.data.text);
      }
    } catch (error) {
      console.error('AI action failed:', error);
    }
  };

  const aiActions = [
    {
      label: 'Regenerate',
      Icon: <RegenerateIcon />,
      onClick: () => handleAIAction(() => regenerateText(value))
    },
    {
      label: 'Change tone',
      Icon: <ChangeToneIcon />,
      children: createToneOptions(value, handleAIAction)
    },
    {
      label: 'Fix grammar & spelling',
      Icon: <GrammarIcon />,
      onClick: () => handleAIAction(() => fixGrammarAndSpelling(value))
    },
    {
      label: 'Make it concise',
      Icon: <ConciseIcon />,
      onClick: () => handleAIAction(() => makeTextConcise(value))
    },
    {
      label: 'Format it',
      Icon: <FormatIcon />,
      onClick: () => handleAIAction(() => improveText(value))
    }
  ];

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
      <div className="flex items-start gap-2">
        <input
          ref={inputRef}
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-[30rem] p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
          placeholder="How would you rate your experience with {{business-name}}?"
        />
        <div className="flex gap-1 items-center">
          <VariableDropdown onSelect={handleVariableSelect} />
          <div className="relative">
            <AIDropdownMinimal     
              actions={aiActions}
              onlyIcon={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const StarsColorField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Stars color</label>
    <div className="space-y-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
        placeholder="Custom #HEX"
      />
      <div className="flex gap-2 flex-wrap">
        {['#FF9500', '#000000', '#00B2FF', '#28a745', '#ffc107', '#dc3545', '#e83e8c', '#6f42c1', '#ffffff'].map((color) => (
          <button
            key={color}
            onClick={() => onChange(color)}
            className={clsx(
              'w-7 h-7 border-2 rounded-lg',
              value === color ? 'border-blue-500' : 'border-gray-300'
            )}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    </div>
  </div>
);

const ButtonsColorField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Buttons color</label>
    <div className="space-y-2">
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
        placeholder="Custom #HEX"
      />
      <div className="flex gap-2 flex-wrap">
        <Tooltip content="Use default color" direction="up">
          <button
            onClick={() => onChange('#007bff')}
            className={clsx('w-7 h-7 border-2 rounded-lg', value === '#007bff' ? 'border-blue-500' : 'border-gray-300')}
            style={{ background: 'linear-gradient(45deg,#007bff,#dc3545,#28a745,#ffc107)' }}
          />
        </Tooltip>
        {['#000000', '#00B2FF', '#28a745', '#ffc107', '#dc3545', '#e83e8c', '#6f42c1', '#ffffff'].map((color) => (
          <button
            key={color}
            onClick={() => onChange(color)}
            className={clsx('w-7 h-7 border-2 rounded-lg', value === color ? 'border-blue-500' : 'border-gray-300')}
            style={{ backgroundColor: color }}
          />
        ))}
      </div>
    </div>
  </div>
);

const DefaultPlatformField = ({ value, onChange }: { value: string; onChange: (value: string) => void }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-2">Default platform</label>
    <p className="text-sm text-gray-600 mb-3">
      Choose the primary platform for display. Other platforms, not set as the default, will be presented in the 'Positive' window, allowing customers to decide whether to leave a review on those platforms or not.
    </p>
    <div className="grid grid-cols-2 gap-3">
      {[
        { value: 'google', label: 'Google' },
        { value: 'yelp', label: 'Yelp' },
      ].map((platform) => (
        <label
          key={platform.value}
          className="flex items-center gap-2 cursor-pointer"
        >
          <input
            type="radio"
            name="defaultPlatform"
            value={platform.value}
            checked={value === platform.value}
            onChange={(e) => onChange(e.target.value)}
            className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
          />
          <span className="text-sm text-gray-900">{platform.label}</span>
        </label>
      ))}
    </div>
  </div>
);

// Export the inline editor component
export const ReviewFormInlineEditor = ({ 
  content, 
  onChange 
}: { 
  content: {
    heading: string;
    description: string;
    starsColor: string;
    buttonsColor: string;
    defaultPlatform: string;
  };
  onChange: (content: any) => void;
}) => (
  <div className="space-y-6">
    <HeadingField 
      value={content.heading} 
      onChange={(value) => onChange({ ...content, heading: value })} 
    />
    <DescriptionField 
      value={content.description} 
      onChange={(value) => onChange({ ...content, description: value })} 
    />
    
    {/* Divider Line */}
    <div className="border-b border-gray-200 mt-4 mb-6"></div>
    
    <StarsColorField 
      value={content.starsColor} 
      onChange={(value) => onChange({ ...content, starsColor: value })} 
    />
    <ButtonsColorField 
      value={content.buttonsColor} 
      onChange={(value) => onChange({ ...content, buttonsColor: value })} 
    />
    
    {/* Divider Line */}
    <div className="border-b border-gray-200 mt-4 mb-6"></div>
    
    <DefaultPlatformField 
      value={content.defaultPlatform} 
      onChange={(value) => onChange({ ...content, defaultPlatform: value })} 
    />
  </div>
);

interface ReviewFormDesignEditorProps {
  open: boolean;
  onClose: () => void;
  onSave: (template: string, editedHtml: string) => void;
  initialTemplate?: string;
}

interface ReviewFormContent {
  heading: string;
  description: string;
  starsColor: string;
  buttonsColor: string;
  defaultPlatform: string;
}

const defaultReviewFormContent: ReviewFormContent = {
  heading: 'Leave a review',
  description: 'How would you rate your experience with {{business-name}}?',
  starsColor: '#FF9500',
  buttonsColor: '#007bff',
  defaultPlatform: 'google',
};

const colorOptions = [
  '#FF9500', // orange
  '#000000', // black
  '#00B2FF', // light blue
  '#28a745', // green
  '#ffc107', // yellow
  '#dc3545', // red
  '#e83e8c', // pink
  '#6f42c1', // purple
  '#ffffff', // white
];

const platformOptions = [
  { value: 'google', label: 'Google', icon: 'G' },
  { value: 'facebook', label: 'Facebook', icon: 'F' },
  { value: 'yelp', label: 'Yelp', icon: 'Y' },
  { value: 'trustpilot', label: 'Trustpilot', icon: 'T' },
  { value: 'new1', label: 'New platform name', icon: 'N' },
  { value: 'new2', label: 'New platform name', icon: 'N' },
  { value: 'new3', label: 'New platform name', icon: 'N' },
  { value: 'new4', label: 'New platform name', icon: 'N' },
  { value: 'new5', label: 'New platform name', icon: 'N' },
];

export const ReviewFormDesignEditor = ({
  open,
  onClose,
  onSave,
  initialTemplate = '',
}: ReviewFormDesignEditorProps) => {
  const dispatch = useAppDispatch();
  const [reviewFormContent, setReviewFormContent] = useState<ReviewFormContent>(defaultReviewFormContent);
  const [businessName, setBusinessName] = useState<string>('Business');
  const [businessLogo, setBusinessLogo] = useState<string>('');

  // Load saved content when modal opens
  useEffect(() => {
    if (open) {
      const storedData = localStorage.getItem('campaignFormData');
      if (storedData) {
        try {
          const parsedData = JSON.parse(storedData);
          if (parsedData.reviewFormDesignContent) {
            // Merge with default values to ensure all new fields exist
            setReviewFormContent({
              ...defaultReviewFormContent,
              ...parsedData.reviewFormDesignContent
            });
          }
          if (parsedData.businessName) {
            setBusinessName(parsedData.businessName);
          }
        } catch (error) {
          // If parsing fails, keep default values
          console.error('Error parsing stored data:', error);
        }
      }
      
      // Fetch business logo from API
      const fetchBusinessLogo = async () => {
        try {
          const response = await getBusinessInfo();
          if (response?.data?.photo?.url) {
            setBusinessLogo(response.data.photo.url);
          }
        } catch (error) {
          console.error('Error fetching business logo:', error);
        }
      };
      
      fetchBusinessLogo();
    }
  }, [open]);

  const handleSave = () => {
    // Save to localStorage immediately
    const storedData = localStorage.getItem('campaignFormData');
    const parsedData = storedData ? JSON.parse(storedData) : {};
    
    // Generate HTML template for review form design
    const reviewFormHtmlTemplate = `<!DOCTYPE html>
<html>
<head>
<style>
body {
  font-family: Arial, sans-serif;
  font-size: 16px;
  margin: 0;
  padding: 20px;
  background-color: #f4f4f4;
  color: #000000;
}
.container {
  background-color: #fff;
  padding: 20px;
  border-radius: 5px;
  box-shadow: 0 2px 5px rgba(0,0,0,.1);
}
.review-header {
  text-align: center;
  margin-bottom: 20px;
}
.review-heading {
  font-size: 24px;
  font-weight: bold;
  color: #111827;
  margin-bottom: 10px;
}
.review-description {
  font-size: 16px;
  color: #374151;
  line-height: 1.5;
}
.stars-container {
  text-align: center;
  margin: 20px 0;
}
.star {
  font-size: 32px;
  cursor: pointer;
  margin: 0 5px;
  transition: color 0.2s;
}
.star.selected {
  color: ${reviewFormContent.starsColor};
}
.star.unselected {
  color: #d1d5db;
}
.platform-selector {
  margin: 20px 0;
  padding: 15px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background-color: #f9fafb;
}
.review-input {
  width: 100%;
  min-height: 100px;
  padding: 15px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-family: inherit;
  font-size: 14px;
  resize: vertical;
}
.submit-button {
  width: 100%;
  padding: 15px;
  background-color: ${reviewFormContent.buttonsColor};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s;
}
.submit-button:hover {
  opacity: 0.9;
}
</style>
</head>
<body>
  <div class="container">
    <div class="review-header">
      <h1 class="review-heading">${reviewFormContent.heading}</h1>
      <p class="review-description">${reviewFormContent.description.replace(/{{business-name}}/g, businessName)}</p>
    </div>
    
    <div class="stars-container">
      <div class="star selected">★</div>
      <div class="star selected">★</div>
      <div class="star selected">★</div>
      <div class="star unselected">★</div>
      <div class="star unselected">★</div>
    </div>
    
    <div class="platform-selector">
      <p><strong>Default Platform:</strong> ${reviewFormContent.defaultPlatform.charAt(0).toUpperCase() + reviewFormContent.defaultPlatform.slice(1)}</p>
    </div>
    
    <div>
      <label for="review-text">Review:</label>
      <textarea 
        id="review-text" 
        class="review-input" 
        placeholder="Describe your experience..."
      ></textarea>
    </div>
    
    <button class="submit-button">
      Leave Review on ${reviewFormContent.defaultPlatform.charAt(0).toUpperCase() + reviewFormContent.defaultPlatform.slice(1)}
    </button>
  </div>
</body>
</html>`;
    
    // Preserve existing form data and only update template-related fields
    const updatedData = {
      ...parsedData,
      updatedReviewFormTemplate: reviewFormHtmlTemplate, // Save as HTML template
      reviewFormDesignContent: reviewFormContent, // Save the design content object
    };
    
    localStorage.setItem('campaignFormData', JSON.stringify(updatedData));
    
    // Dispatch to Redux
    dispatch(updateFormData(updatedData));
    
    onSave(initialTemplate, reviewFormHtmlTemplate);
    onClose();
  };

  const getSelectedPlatform = () => {
    return platformOptions.find(p => p.value === reviewFormContent.defaultPlatform) || platformOptions[0];
  };

  return (
    <BaseDialog open={open} onClose={onClose} size="max-w-6xl">
      <div className="flex flex-col h-[90vh] bg-white rounded-lg overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-bold text-gray-900">Design settings</h2>
          </div>
          <button
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            Save & continue
          </button>
        </div>

        {/* Subtitle */}
        <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
          <p className="text-sm text-gray-600">
            Customize the design of the window for the customer to leave a public review.
          </p>
        </div>

        {/* Main Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Design Settings */}
          <div className="w-1/2 p-6 overflow-y-auto border-r border-gray-200">
            <div className="space-y-8">
              {/* Heading */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Heading
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={reviewFormContent.heading}
                    onChange={(e) => setReviewFormContent(prev => ({ ...prev, heading: e.target.value }))}
                    placeholder="Leave a review"
                    className="w-[30rem] p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 bg-gray-50"
                  />
                  <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-purple-100">
                    <span className="text-sm font-mono text-purple-600">&lt;/&gt;</span>
                  </button>
                  <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-purple-100">
                    <span className="text-sm text-purple-600">✨</span>
                  </button>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Description
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={reviewFormContent.description}
                    onChange={(e) => setReviewFormContent(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="How would you rate your experience with {{business-name}}?"
                    className="w-[30rem] p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 bg-gray-50"
                  />
                  <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-purple-100">
                    <span className="text-sm font-mono text-purple-600">&lt;/&gt;</span>
                  </button>
                  <button className="p-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors bg-purple-100">
                    <span className="text-sm text-purple-600">✨</span>
                  </button>
                </div>
              </div>

              {/* Divider Line */}
              <div className="border-b border-gray-200 mt-4 mb-6"></div>

              {/* Stars Color */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Stars color
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    value={reviewFormContent.starsColor}
                    onChange={(e) => setReviewFormContent(prev => ({ ...prev, starsColor: e.target.value }))}
                    placeholder="Custom #HEX"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 bg-gray-50"
                  />
                  <div className="flex gap-2 flex-wrap">
                    {colorOptions.map((color) => (
                      <button
                        key={color}
                        onClick={() => setReviewFormContent(prev => ({ ...prev, starsColor: color }))}
                        className={clsx(
                          'w-8 h-8 border-2 transition-colors',
                          reviewFormContent.starsColor === color ? 'border-blue-500' : 'border-gray-300'
                        )}
                        style={{ backgroundColor: color }}
                        aria-label={`Select color ${color}`}
                      />
                    ))}
                  </div>
                </div>
              </div>


            </div>
          </div>

          {/* Right Panel - Preview */}
          <div className="w-1/2 p-6 overflow-y-auto bg-gray-50">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Preview</h3>
            </div>
            
            {/* Header - Outside white card */}
            <div className="max-w-[845px] mx-auto bg-white p-4 px-20 mb-4">
              <div className="flex items-center justify-center gap-2">
                {businessLogo ? (
                  <div className="w-12 h-12 rounded-lg flex items-center justify-center overflow-hidden bg-white">
                    <img src={businessLogo} alt="Business Logo" className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center">
                    <span className="text-white font-bold text-lg">{businessName.charAt(0)}</span>
                  </div>
                )}
                <div className="text-left">
                  <div className="font-semibold text-gray-900">{businessName}</div>
                  <div className="text-sm text-gray-600">123 Main Street, Suite 456, Anytown, USA</div>
                </div>
              </div>
            </div>

            {/* White Content Card */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                {/* Review Request */}
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 mb-2">
                    {reviewFormContent.heading}
                  </h2>
                  <p className="text-gray-600 mb-4">
                    {reviewFormContent.description.replace(/{{business-name}}/g, businessName)}
                  </p>
                  
                  {/* Stars */}
                  <div className="mb-4">
                    <div className="text-sm text-gray-600 mb-2">Select stars to rate</div>
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          className="text-2xl"
                          style={{ color: star <= 2 ? reviewFormContent.starsColor : '#d1d5db' }}
                        >
                          ★
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Platform Dropdown */}
                  <div className="mb-4">
                    <div className="flex items-center gap-2 p-2 border border-gray-300 rounded-lg">
                      <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" />
                      </svg>
                      <span className="text-sm">Public</span>
                      <span className="ml-auto">▼</span>
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Review Input */}
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Review</label>
                  <div className="relative">
                    <textarea
                      placeholder="Describe your case so we can help you solve it"
                      className="w-full p-3 border border-gray-300 rounded-lg resize-none"
                      rows={4}
                    />
                    <button className="absolute top-2 right-2 text-xs text-blue-600 hover:text-blue-700 px-2 py-1 rounded">
                      Improve with AI
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">
                    By leaving this review you accept our{' '}
                    <a href="#" className="text-blue-600 hover:underline">Privacy Policy</a>
                  </p>
                </div>

                {/* Media Attachment */}
                <div className="mb-6">
                  <button className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span className="text-sm">Attach photos</span>
                  </button>
                  <div className="flex gap-2 mt-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center">
                        <FilePhotoIcon className="w-7 h-7 text-gray-500" />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  className="w-full py-2 px-4 rounded-lg text-white font-medium flex items-center justify-center gap-2"
                  style={{ backgroundColor: reviewFormContent.buttonsColor }}
                >
                  <div className="w-5 h-5 bg-white rounded flex items-center justify-center">
                    <span className="text-xs font-bold text-blue-600">G</span>
                  </div>
                  Leave this review on {getSelectedPlatform().label}
                </button>
              </div>
            </div>
            
            {/* Footer */}
            <div className="text-center mt-4">
              <p className="text-xs text-gray-500">Powered by Echo Ratings</p>
            </div>
          </div>
        </div>
      </div>
    </BaseDialog>
  );
};
